// Macchina a stati PURA per una connessione SSE resiliente all'auth.
//
// EventSource nativo (1) non puo' rinnovare il cookie access scaduto e (2) ritenta da
// solo in loop: dopo 15 min il JWT vp_at scade e ogni riconnessione riceve 401 →
// disconnessioni continue + errori in console. Qui gestiamo noi la riconnessione:
// alla prima 'error' chiudiamo l'EventSource (stop al retry nativo cieco), VERIFICHIAMO
// la sessione e riconnettiamo con backoff.
//
// La verifica non e' un rinnovo forzato. Rinnovare a ogni errore SSE significava ruotare
// il refresh token a ogni blip di rete, risveglio del portatile o riavvio del backend —
// in produzione fino a un rinnovo ogni 45 secondi, contro i ~4 all'ora previsti dalla
// scadenza dell'access. Ogni rotazione e' una finestra in cui le richieste in volo col
// cookie precedente vengono rifiutate. Ora si fa una richiesta di prova che passa
// dall'interceptor Axios: il rinnovo parte SOLO se il token e' davvero scaduto.
//
// Tutte le dipendenze sono iniettabili per renderla testabile fuori dal browser.

export function safeParse(s)
{
	try
	{
		return JSON.parse(s);
	}
	catch
	{
		return s;
	}
}

const defaultBackoff = (attempt) => Math.min(500 * 2 ** attempt, 10000);

/**
 * Apre e mantiene viva una connessione SSE. Ritorna una funzione close() idempotente.
 *
 * @param {string} url
 * @param {object} opts
 * @param {Record<string,Function>} [opts.listeners]  handler per event-name
 * @param {Function} [opts.onOpen]                    connessione aperta
 * @param {Function} [opts.onError]                   ogni disconnessione/errore
 * @param {Function} [opts.onFatal]                   sessione non recuperabile (stop)
 * @param {Function} opts.EventSourceImpl             costruttore EventSource
 * @param {Function} opts.checkSession                () => Promise (single-flight); rigetta con 401 se la sessione e' morta
 * @param {Function} [opts.notifyUnauthorized]        porta l'app in stato anon
 * @param {Function} [opts.scheduleTimeout]           (fn, ms) => id
 * @param {Function} [opts.cancelTimeout]             (id) => void
 * @param {Function} [opts.backoff]                   (attempt) => ms
 */
export function createManagedSSEConnection(url, opts = {})
{
	const {
		listeners = {},
		onOpen,
		onError,
		onFatal,
		EventSourceImpl,
		checkSession,
		notifyUnauthorized,
		scheduleTimeout = (fn, ms) => setTimeout(fn, ms),
		cancelTimeout = (id) => clearTimeout(id),
		backoff = defaultBackoff
	} = opts;

	let es = null;
	let closed = false;
	let attempt = 0;
	let timer = null;

	const teardownEs = () =>
	{
		if (!es)
			return;
		es.onopen = null;
		es.onerror = null;
		try { es.close(); }
		catch { /* gia' chiuso */ }
		es = null;
	};

	const connect = () =>
	{
		if (closed)
			return;

		es = new EventSourceImpl(url, {withCredentials: true});

		es.onopen = () =>
		{
			attempt = 0; // connessione stabile → azzera il backoff
			if (onOpen) onOpen();
		};

		for (const [evt, fn] of Object.entries(listeners))
		{
			if (typeof fn === "function")
				es.addEventListener(evt, (e) => fn(safeParse(e.data)));
		}

		es.onerror = () => { handleError(); };
	};

	const scheduleReconnect = () =>
	{
		if (closed)
			return;
		timer = scheduleTimeout(connect, backoff(attempt));
	};

	async function handleError()
	{
		if (closed)
			return;

		teardownEs();
		if (onError) onError();
		if (closed)
			return;

		attempt += 1;

		// EventSource non espone lo status HTTP dell'errore: lo chiediamo con una richiesta
		// di prova (single-flight → una sola verifica anche con piu' connessioni SSE in
		// errore). E' l'interceptor Axios a decidere se serve un rinnovo.
		// - 401          → sessione morta: logout, niente altri tentativi.
		// - errore rete  → server irraggiungibile: NON fare logout, ritenta col backoff.
		// - ok           → era solo la connessione SSE: riconnetti, nessuna rotazione.
		try
		{
			if (typeof checkSession === "function")
				await checkSession();
		}
		catch (e)
		{
			if (e?.response?.status === 401)
			{
				closed = true;
				if (notifyUnauthorized) notifyUnauthorized();
				if (onFatal) onFatal();
				return;
			}
			// altrimenti: errore di rete/server → si ritenta
		}

		scheduleReconnect();
	}

	// Primo connect differito di un tick: assorbe il doppio mount di StrictMode (dev)
	// senza aprire una connessione transitoria che verrebbe subito abortita.
	timer = scheduleTimeout(connect, 0);

	return () =>
	{
		closed = true;
		if (timer != null)
			cancelTimeout(timer);
		teardownEs();
	};
}
